import { useEffect, useRef, useState, Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Send, ArrowUpRight, ExternalLink } from "lucide-react";
import { NexusMark } from "@/components/site/NexusMark";
import { CONTACT, whatsappLink, OPEN_HOUSE } from "@/data/content";
import { useEnquiry } from "@/context/EnquiryContext";
import { pixel, pixelOnce } from "@/lib/pixel";

const API = process.env.REACT_APP_BACKEND_URL;
const ACTION_RE = /\[\[ACTION:(ENQUIRE|WHATSAPP|OPENHOUSE)\]\]/g;
const SUGGEST_RE = /\[\[SUGGEST:([^\]]*)\]\]/;
const TAG_RE = /\[\[[^\]]*\]\]/g;
const PARTIAL_TAG_RE = /\[\[[^\]]*$/;
const TOKEN_RE = /(https?:\/\/[^\s]+|\/[a-z][a-z0-9/#-]*)/g;

const DEFAULT_CTX = {
  greeting: "Hi, I'm Ask Nexus 👋 I can help with our team, services, events, or joining Affinity. What would you like to know?",
  suggestions: ["What is Affinity Group?", "Tell me about the IFA Open House", "What services do you offer?", "I'd like to join as an advisor"],
  nudge: "Have a question about Affinity? I'm right here 👋",
};

const PAGE_CTX = [
  {
    match: (p) => p.startsWith("/careers"),
    greeting: "Hi, I'm Ask Nexus 👋 Exploring a career in advisory? I can tell you about the IFA Recruitment Open House, the support advisors get, or how to join. What would you like to know?",
    suggestions: ["Tell me about the IFA Open House", "What support do advisors get?", "Do I need experience to join?", "I'd like to join as an advisor"],
    nudge: "Curious about the IFA Recruitment Open House? Ask me anything 👋",
  },
  {
    match: (p) => p.startsWith("/services"),
    greeting: "Hi, I'm Ask Nexus 👋 Thinking about your financial planning? I can explain how we help individuals, families and businesses, or connect you with an advisor. How can I help?",
    suggestions: ["What services do you offer?", "Help me plan for retirement", "Tell me about estate planning", "I'd like to speak to an advisor"],
    nudge: "Have a financial planning question? I'm happy to help 👋",
  },
  {
    match: (p) => p.startsWith("/events"),
    greeting: "Hi, I'm Ask Nexus 👋 Want to join one of our sessions? I can share upcoming seminars and webinars, or the IFA Recruitment Open House. What interests you?",
    suggestions: ["What events are coming up?", "Tell me about the IFA Open House", "Estate planning webinar details", "Register me for a session"],
    nudge: "Want to join an upcoming seminar? Ask me how 👋",
  },
  {
    match: (p) => p.startsWith("/team"),
    greeting: "Hi, I'm Ask Nexus 👋 Looking for the right advisor? I can help you find someone on our team or tell you about our leadership. Who can I help you find?",
    suggestions: ["Who leads Affinity?", "Tell me about Jian Da", "Find me an advisor", "Connect me with an advisor"],
    nudge: "Looking for the right advisor? I can help you find one 👋",
  },
];

const getContext = (p) => PAGE_CTX.find((c) => c.match(p)) || DEFAULT_CTX;

// Tunable proactive-nudge behaviour
const NUDGE = {
  delayMs: 6000, // show after this long on the page…
  scrollDepth: 0.3, // …or once the visitor scrolls this far (30%), whichever comes first
  cooldownMs: 24 * 60 * 60 * 1000, // don't nudge the same visitor again within this window
  autoHideMs: 22000, // auto-dismiss if ignored
};

const sessionKey = () => {
  let id = localStorage.getItem("affinity-chat-session");
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem("affinity-chat-session", id);
  }
  return id;
};

const parseMessage = (raw) => {
  const actions = [];
  let m;
  ACTION_RE.lastIndex = 0;
  while ((m = ACTION_RE.exec(raw))) actions.push(m[1]);
  const sm = SUGGEST_RE.exec(raw);
  const suggestions = sm ? sm[1].split("|").map((q) => q.trim()).filter(Boolean).slice(0, 3) : [];
  const text = raw.replace(TAG_RE, "").replace(PARTIAL_TAG_RE, "").trim();
  return { text, actions: [...new Set(actions)], suggestions };
};

const renderInline = (str, keyBase) => {
  const parts = str.split(TOKEN_RE);
  return parts.map((part, i) => {
    if (!part) return null;
    if (part.startsWith("http")) {
      const clean = part.replace(/[.,)]+$/, "");
      return <a key={`${keyBase}-${i}`} href={clean} target="_blank" rel="noopener noreferrer" className="text-[var(--gold-2)] font-semibold hover:underline break-words">{clean.replace(/^https?:\/\//, "")}</a>;
    }
    if (part.startsWith("/")) {
      return <Link key={`${keyBase}-${i}`} to={part} className="text-[var(--gold-2)] font-semibold hover:underline">{part}</Link>;
    }
    return <Fragment key={`${keyBase}-${i}`}>{part}</Fragment>;
  });
};

const renderLine = (line, key) => {
  const clean = line.replace(/^#{1,6}\s+/, "").replace(/^[-*]\s+/, "• ");
  const segs = clean.split(/\*\*(.+?)\*\*/g);
  return (
    <p key={key} className="m-0">
      {segs.map((seg, i) =>
        i % 2 === 1
          ? <strong key={`b-${i}`} className="font-semibold text-[var(--cream)]">{renderInline(seg, `b${i}`)}</strong>
          : <Fragment key={`t-${i}`}>{renderInline(seg, `t${i}`)}</Fragment>
      )}
    </p>
  );
};

const MessageBubble = ({ role, content, streaming, isLast, onSuggest }) => {
  const { openEnquiry } = useEnquiry();
  if (role === "user") {
    return (
      <div className="flex justify-end" data-testid="chat-message-user">
        <div className="max-w-[82%] rounded-2xl rounded-br-md bg-[var(--gold)] text-[var(--navy)] px-4 py-2.5 text-[13.5px] font-medium leading-relaxed">{content}</div>
      </div>
    );
  }
  const { text, actions, suggestions } = parseMessage(content);
  const lines = text.split("\n").filter((l) => l.trim() !== "");
  const thinking = streaming && lines.length === 0;
  return (
    <div className="flex flex-col items-start gap-2.5" data-testid="chat-message-assistant">
      <div className="flex items-end gap-2 max-w-[92%]">
      <NexusMark size={22} dark className={`mb-1 ${streaming ? "nexus-pulse" : ""}`} />
      <div className="max-w-[86%] rounded-2xl rounded-bl-md bg-[rgba(244,241,234,.06)] border border-white/8 text-[rgba(244,241,234,.92)] px-4 py-3 text-[13.5px] leading-relaxed space-y-2">
        {thinking ? (
          <span className="inline-flex items-center gap-2 text-[12.5px] text-[rgba(244,241,234,.6)]" data-testid="chat-thinking" aria-live="polite">
            <span className="italic font-serif text-[14px] text-[var(--gold-2)]">Nexus is thinking</span>
            <span className="inline-flex gap-1" aria-hidden>{[0, 1, 2].map((i) => <i key={i} className="nexus-dot" style={{ animationDelay: `${i * 0.18}s` }} />)}</span>
          </span>
        ) : lines.map((l, i) => renderLine(l, i))}
        {streaming && !thinking && <span className="inline-block w-1.5 h-4 align-middle bg-[var(--gold-2)] animate-pulse ml-0.5" />}
      </div>
      </div>
      {!streaming && actions.length > 0 && (
        <div className="flex flex-wrap gap-2 pl-8">
          {actions.includes("OPENHOUSE") && (
            <button type="button" onClick={() => openEnquiry("event", { event: OPEN_HOUSE.enquiryEvent, headcount: "1" })} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--gold)] text-[var(--navy)] px-3.5 py-1.5 text-[12px] font-bold hover:-translate-y-0.5 transition-transform" data-testid="chat-action-openhouse">
              Register for the Open House <ArrowUpRight size={13} />
            </button>
          )}
          {actions.includes("ENQUIRE") && (
            <button type="button" onClick={() => openEnquiry("advisor")} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--gold)] text-[var(--navy)] px-3.5 py-1.5 text-[12px] font-bold hover:-translate-y-0.5 transition-transform" data-testid="chat-action-enquire">
              Enquire now <ArrowUpRight size={13} />
            </button>
          )}
          {actions.includes("WHATSAPP") && (
            <a href={whatsappLink("Hi Affinity Group, I have a question I'd like to discuss.")} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(212,168,77,.5)] text-[var(--gold-2)] px-3.5 py-1.5 text-[12px] font-bold hover:bg-[rgba(212,168,77,.1)] transition-colors" data-testid="chat-action-whatsapp">
              WhatsApp {CONTACT.whatsappDisplay} <ExternalLink size={12} />
            </a>
          )}
        </div>
      )}
      {!streaming && isLast && suggestions.length > 0 && (
        <div className="flex flex-wrap gap-2 pl-8 pt-0.5" data-testid="chat-followups">
          <span className="w-full font-mono text-[9px] tracking-[.18em] uppercase text-[rgba(244,241,234,.4)]">Keep exploring</span>
          {suggestions.map((q, i) => (
            <button key={q} type="button" onClick={() => onSuggest(q)} className="rounded-full border border-[rgba(212,168,77,.35)] text-[rgba(244,241,234,.85)] px-3 py-1.5 text-[12px] hover:border-[var(--gold)] hover:bg-[rgba(212,168,77,.1)] hover:text-[var(--gold-2)] transition-colors animate-[chatIn_.35s_ease]" style={{ animationDelay: `${i * 70}ms` }} data-testid={`chat-followup-${i}`}>{q}</button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ChatWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [{ role: "assistant", content: getContext(window.location.pathname).greeting }]);
  const [suggestions, setSuggestions] = useState(() => getContext(window.location.pathname).suggestions);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [nudge, setNudge] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const session = useRef(sessionKey());
  const openRef = useRef(false);

  useEffect(() => { openRef.current = open; }, [open]);

  useEffect(() => {
    const last = Number(localStorage.getItem("affinity-nudge-at") || 0);
    if (open || Date.now() - last < NUDGE.cooldownMs) return;
    let shown = false;
    let hideT;
    const trigger = () => {
      if (shown || openRef.current) return;
      shown = true;
      setNudge(true);
      localStorage.setItem("affinity-nudge-at", String(Date.now()));
      hideT = setTimeout(() => setNudge(false), NUDGE.autoHideMs);
      window.removeEventListener("scroll", onScroll);
      clearTimeout(delayT);
    };
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      if (h > 0 && window.scrollY / h >= NUDGE.scrollDepth) trigger();
    };
    const delayT = setTimeout(trigger, NUDGE.delayMs);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearTimeout(delayT); clearTimeout(hideT); window.removeEventListener("scroll", onScroll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissNudge = () => setNudge(false);
  const openFromNudge = () => { setNudge(false); setOpen(true); };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    setNudge(false);
    if (!messages.some((m) => m.role === "user")) {
      const ctx = getContext(location.pathname);
      setMessages([{ role: "assistant", content: ctx.greeting }]);
      setSuggestions(ctx.suggestions);
    }
    if (inputRef.current) inputRef.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    pixelOnce("chat-contact", "Contact", { content_name: "Ask Nexus chat", content_category: "chatbot" }, { server: true });
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "", streaming: true }]);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session.current, message: text }),
      });
      if (res.status === 429) {
        let msg = "You're sending messages a little too quickly. Please take a short break and try again.";
        try { const j = await res.json(); if (j.detail) msg = j.detail; } catch (e) { /* ignore */ }
        setMessages((prev) => { const n = [...prev]; n[n.length - 1] = { role: "assistant", content: `${msg} [[ACTION:WHATSAPP]]`, streaming: false }; return n; });
        setBusy(false);
        return;
      }
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      let errored = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let data;
          try { data = JSON.parse(line.slice(5).trim()); } catch (e) { continue; }
          if (data.delta) {
            acc += data.delta;
            setMessages((prev) => { const n = [...prev]; n[n.length - 1] = { role: "assistant", content: acc, streaming: true }; return n; });
          }
          if (data.error) errored = true;
        }
      }
      if (/\[\[LEAD\b/.test(acc)) pixelOnce("chat-lead", "Lead", { content_name: "Chatbot lead", content_category: "chatbot" }, { eventId: `chatlead_${session.current}` });
      const finalText = errored && !acc ? "Sorry, I ran into a hiccup. Please try again, or reach us on WhatsApp. [[ACTION:WHATSAPP]]" : acc;
      setMessages((prev) => { const n = [...prev]; n[n.length - 1] = { role: "assistant", content: finalText, streaming: false }; return n; });
    } catch (e) {
      setMessages((prev) => { const n = [...prev]; n[n.length - 1] = { role: "assistant", content: "Sorry, I couldn't reach the assistant just now. Please try again in a moment, or contact us on WhatsApp. [[ACTION:WHATSAPP]]", streaming: false }; return n; });
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {nudge && !open && (
        <div
          data-testid="chat-nudge"
          className="fixed z-[72] right-[76px] md:right-[92px] bottom-6 md:bottom-8 max-w-[248px] animate-[chatIn_.35s_ease]"
        >
          <div className="relative rounded-2xl rounded-br-md bg-[var(--cream)] text-[var(--navy)] shadow-[0_18px_44px_rgba(4,10,11,.4)] pl-4 pr-8 py-3">
            <button type="button" onClick={dismissNudge} aria-label="Dismiss" data-testid="chat-nudge-close" className="absolute top-1.5 right-1.5 grid place-items-center w-6 h-6 rounded-full text-[rgba(7,20,22,.5)] hover:text-[var(--navy)] hover:bg-black/5 transition-colors"><X size={13} /></button>
            <button type="button" onClick={openFromNudge} data-testid="chat-nudge-open" className="block text-left text-[13px] font-semibold leading-snug">
              {getContext(location.pathname).nudge}
            </button>
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close Ask Nexus chat" : "Open Ask Nexus chat"}
        data-testid="chat-launcher"
        className="fixed z-[72] right-5 bottom-5 md:right-7 md:bottom-7 grid place-items-center w-14 h-14 rounded-full bg-[var(--gold)] text-[var(--navy)] shadow-[0_16px_40px_rgba(212,168,77,.4)] hover:-translate-y-1 transition-transform duration-300"
      >
        {open ? <X size={24} /> : <NexusMark size={56} />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-[72] right-3 bottom-24 md:right-7 md:bottom-28 w-[calc(100vw-1.5rem)] sm:w-[400px] h-[min(600px,calc(100vh-8rem))] flex flex-col rounded-3xl overflow-hidden border border-[rgba(212,168,77,.28)] bg-[var(--navy-2)] shadow-[0_30px_80px_rgba(4,10,11,.6)] animate-[chatIn_.3s_ease]"
        >
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/8 bg-[var(--navy)]">
            <NexusMark size={40} />
            <div className="min-w-0">
              <p className="m-0 font-serif text-lg leading-tight text-[var(--cream)]">Ask Nexus</p>
              <p className="m-0 font-mono text-[10px] tracking-[.14em] uppercase text-[rgba(244,241,234,.5)]">Your guide to Affinity Group</p>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="ml-auto grid place-items-center w-9 h-9 rounded-full text-[rgba(244,241,234,.6)] hover:text-[var(--gold-2)] transition-colors" aria-label="Close chat" data-testid="chat-close"><X size={18} /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {messages.map((m, i) => (
              <MessageBubble key={i} role={m.role} content={m.content} streaming={m.streaming} isLast={i === messages.length - 1} onSuggest={send} />
            ))}
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {suggestions.map((s, i) => (
                  <button key={i} type="button" onClick={() => send(s)} className="rounded-full border border-white/12 text-[rgba(244,241,234,.8)] px-3 py-1.5 text-[12px] hover:border-[rgba(212,168,77,.5)] hover:text-[var(--gold-2)] transition-colors" data-testid={`chat-suggestion-${i}`}>{s}</button>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-white/8 bg-[var(--navy)]">
            <div className="flex items-end gap-2 rounded-2xl border border-white/12 bg-[rgba(244,241,234,.04)] px-3 py-2 focus-within:border-[rgba(212,168,77,.5)] transition-colors">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Ask about Affinity…"
                data-testid="chat-input"
                className="flex-1 resize-none bg-transparent text-[13.5px] text-[var(--cream)] placeholder:text-[rgba(244,241,234,.4)] outline-none max-h-24 py-1"
              />
              <button type="button" onClick={() => send()} disabled={!input.trim() || busy} className="grid place-items-center w-9 h-9 rounded-xl bg-[var(--gold)] text-[var(--navy)] disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5 transition-transform shrink-0" aria-label="Send message" data-testid="chat-send"><Send size={16} /></button>
            </div>
            <p className="m-0 mt-2 text-center text-[10px] text-[rgba(244,241,234,.35)]">AI assistant · not licensed financial advice</p>
          </div>
        </div>
      )}
    </>
  );
};
